import LoadingButton from '@mui/lab/LoadingButton';
import { MenuItem } from '@mui/material';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import ControlledCheckbox from '~components/Form/ControlledCheckbox';
import organisations from '~organisations';
import { CallBackPublicHoliday, DiallingHours, Disposition, OutcomeType } from '~pages/CampaignManagement/domain';
import { getTimezoneList } from '~pages/Dialler/api';
import { Callback } from '~pages/Dialler/domain';
import { useAppConfiguration } from '~providers/AppConfigurationProvider';
import { useConnect } from '~providers/ConnectProvider';
import { buildShouldDisableTime } from '~utils/DiallingHours';
import { convertDictValuesToString } from '~utils/Functions';

import LoadingButtonDropdown from '../LoadingButtonDropdown';

type ChannelType = 'voice' | 'chat';

interface OutcomeCaptureProps {
  orgReference: string;
  channelType: ChannelType;
  campaignId?: number;
  dispositions?: Disposition[];
  diallingHours?: DiallingHours;
  publicHolidays?: CallBackPublicHoliday[];
  exclusionLists?: string[];
  timezone?: string;
  agentUsername?: string;
  isUnknownLead?: boolean;
  contactName?: string;
  endpoint?: string;
  disableSubmit?: boolean;
  disableNewLead?: boolean;
  onSubmit: (data: Outcome, switchToStatus: string | undefined) => Promise<void>;
}

// Note: lead's endpoint comes from our amazon connect contact object
interface NewLead {
  endpoint: string;
  name: string;
  externalId: string;
  timezone: string;
}

export interface Outcome {
  dispositionCode: string;
  dispositionSubCode: string;
  hasSystemIssue: boolean;
  systemIssueDescription: string;
  exclusionList?: string;
  callback?: Callback;
  newLead?: NewLead;
  attributes: Record<string, any>;
}
export interface OutcomesFormProps {
  formSubmitting: boolean;
  channelType: ChannelType;
}

// Used for disabling dates within disableDates function
const dayToWeekdayNumber: { [key: string]: number } = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

// Used for showing error messages for selected date range within onValidation function
const weekdayNumberToDay: { [key: number]: string } = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

const formatTimeComponent = (component: number) => (component < 10 ? `0${component}` : component.toString());

interface FormValues {
  disposition: Disposition | null;
  hasSystemIssue: boolean;
  leadEndpoint: string;
  leadName: string;
  leadExternalId: string;
  leadTimezone: string;
  callbackEndpoint: string;
  callbackAssignment: string;
  systemIssueDescription: string;
  forAgent: string | null;
  scheduled: string;
  notes: string;
  exclusionList: string;
  [key: string]: any;
}

const OutcomeCapture = ({
  orgReference,
  channelType,
  campaignId,
  dispositions,
  diallingHours,
  publicHolidays,
  exclusionLists,
  timezone,
  agentUsername,
  isUnknownLead,
  contactName,
  endpoint,
  disableSubmit,
  disableNewLead,
  onSubmit,
}: OutcomeCaptureProps) => {
  const appConfig = useAppConfiguration();
  const { agentStateList } = useConnect();
  const [viewFlag, setViewFlag] = useState<
    OutcomeType.Callback | OutcomeType.Snoozed | OutcomeType.Excluded | undefined
  >(undefined);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [requireLeadCreation, setRequireLeadCreation] = useState<boolean>(false);
  const [timezoneList, setTimezoneList] = useState<string[]>([]);
  const [timezoneFetchError, setTimezoneFetchError] = useState<string>('');
  const AdditionalFields =
    campaignId !== undefined ? organisations[orgReference]?.additionalOutcomeCaptureFields[campaignId] : undefined;

  const methods = useForm<FormValues>({
    defaultValues: {
      disposition: null,
      hasSystemIssue: false,
      leadEndpoint: '',
      leadName: '',
      leadExternalId: '',
      leadTimezone: '',
      callbackEndpoint: '',
      callbackAssignment: '',
      forAgent: null,
      scheduled: '',
      notes: '',
      exclusionList: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const {
    watch,
    control,
    setValue,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = methods;
  const [disposition, hasSystemIssue, callbackEndpoint, callbackAssignment] = watch([
    'disposition',
    'hasSystemIssue',
    'callbackEndpoint',
    'callbackAssignment',
  ]);

  // Fetch timezone list
  useEffect(() => {
    if (isUnknownLead) {
      const fetchTimezoneList = async () => {
        setTimezoneFetchError('');

        let resp: string[];
        try {
          resp = await getTimezoneList(appConfig.web.timezonePrefixes);
        } catch (e) {
          setTimezoneFetchError('Unable to fetch timezone list');
          return;
        }

        setTimezoneList(resp);
        setValue('leadTimezone', appConfig.defaultTimezone);
      };

      fetchTimezoneList();

      return () => {
        setTimezoneFetchError('');
        setTimezoneList([]);
      };
    }
  }, [isUnknownLead, appConfig.web.timezonePrefixes]);

  // Dynamically add/ remove unknown lead collection detail fields
  useEffect(() => {
    if (isUnknownLead === true) {
      if (disposition?.outcome === OutcomeType.Callback) {
        if (!requireLeadCreation) {
          setRequireLeadCreation(true);
        }
        setValue('leadEndpoint', getValues().callbackEndpoint);
      } else if (requireLeadCreation) {
        setValue('leadEndpoint', '');
        setRequireLeadCreation(false);
      }
      return;
    }
  }, [isUnknownLead, disposition, callbackEndpoint]);

  // Dynamically add/ remove new fields to form schema dependent on campaign and lead status type
  useEffect(() => {
    if (channelType === 'voice') {
      if (disposition) {
        // If dispositions' object does not exist, then we skip everything below this
        if (dispositions === undefined) {
          console.error('OutcomeCapture Disposition fields Effect: dispositions do not exist');
          return;
        }
        const dispositionCode = disposition.code;
        const dispositionSubCode = disposition.subCode;
        const dispo = dispositions.find(
          (dispo) => dispo.code === dispositionCode && dispo.subCode === dispositionSubCode,
        );

        if (dispo?.outcome === OutcomeType.Callback || dispo?.outcome === OutcomeType.Snoozed) {
          const username = agentUsername || '';
          setValue('forAgent', callbackAssignment ? username : null);
          setValue('callbackEndpoint', endpoint && endpoint !== 'anonymous' ? endpoint : '');
          setViewFlag(dispo?.outcome === OutcomeType.Callback ? OutcomeType.Callback : OutcomeType.Snoozed);
          return;
        }

        if (dispo?.outcome === OutcomeType.Excluded) {
          setViewFlag(OutcomeType.Excluded);
          return;
        }
        setViewFlag(undefined);
      }
    }
  }, [disposition]);

  // Dynamic assignment of callback agent. Can either be the current agent or null (assigned to another agent via leads engine)
  useEffect(() => {
    // If for any reason the passed in username is undefined, we want to default to the null case
    const username = agentUsername || '';
    setValue('forAgent', callbackAssignment ? username : null);
  }, [callbackAssignment]);

  // Applies the same dialling hours that the scheduled field's validate rule enforces, so out of
  // hours times are never offered by the clock rather than only rejected after being picked.
  const shouldDisableTime = useMemo(() => buildShouldDisableTime(diallingHours), [diallingHours]);

  const disableDates = (date: DateTime | null) => {
    // Disable public holidays if not allowed
    if (!diallingHours?.allowPublicHolidays) {
      if (publicHolidays === undefined) return false;

      for (const ph of publicHolidays) {
        if (date && ph.year === date.year && ph.month === date.month && ph.day === date.day) {
          return true;
        }
      }
    }

    // Disable specific days of the week if no dialing day hours exist
    for (const day in diallingHours?.diallingDays) {
      if (diallingHours?.diallingDays[day] === null && dayToWeekdayNumber[day] === date?.weekday) {
        return true;
      }
    }

    return false;
  };

  const handleOutcomeSubmit = (switchToStatus?: string) =>
    handleSubmit(async (formData: Record<string, any>) => {
      // Remove all expected fields, leaving the other object to be custom attributes
      const {
        disposition,
        exclusionList,
        callbackAssignment,
        forAgent,
        scheduled,
        callbackEndpoint,
        notes,
        hasSystemIssue,
        systemIssueDescription,
        leadEndpoint,
        leadName,
        leadExternalId,
        leadTimezone,
        ...other
      } = formData;
      const attributes = convertDictValuesToString(other);
      const createNewLead = leadEndpoint != '' && leadName != '' && leadExternalId != '' && leadTimezone != '';

      const data: Outcome = {
        dispositionCode: disposition.code,
        dispositionSubCode: disposition.subCode,
        hasSystemIssue: hasSystemIssue,
        systemIssueDescription: systemIssueDescription ?? '',
        exclusionList: viewFlag === OutcomeType.Excluded ? exclusionList : undefined,
        callback:
          viewFlag === OutcomeType.Callback || viewFlag === OutcomeType.Snoozed
            ? {
                forAgent: forAgent,
                // Appending timezone info
                // Note: If this is an unknown inbound call, the timezone field will be undefined,
                // so we use the timezone of the new lead we create with this callback outcome
                scheduled: DateTime.fromISO(scheduled, { zone: timezone || leadTimezone }).toISO(),
                endpoint: callbackEndpoint,
                notes: notes,
              }
            : undefined,
        newLead:
          isUnknownLead && createNewLead
            ? {
                endpoint: leadEndpoint,
                name: leadName,
                externalId: leadExternalId,
                timezone: leadTimezone,
              }
            : undefined,
        attributes: attributes,
      };

      try {
        setFormSubmitting(true);
        await onSubmit(data, switchToStatus);
      } catch (e) {
        return;
      } finally {
        setFormSubmitting(false);
      }

      setViewFlag(undefined);
      reset();
    });

  let scheduledHelpTextDefault = timezone ? `(local time in ${timezone})` : '(local time in new leads timezone)';
  if (timezone && contactName) {
    scheduledHelpTextDefault = `(${contactName}'s time in ${timezone})`;
  }
  const scheduledHelperText = errors.scheduled ? errors.scheduled.message : scheduledHelpTextDefault;
  const exclusionListItems = exclusionLists?.map((value, index) => (
    <MenuItem key={index} value={value}>
      {value}
    </MenuItem>
  ));

  return (
    <FormProvider {...methods}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Controller
            name='disposition'
            control={control}
            rules={{ required: 'A disposition is required' }}
            render={({ field: { onChange, value, name } }) => (
              <Autocomplete
                fullWidth
                onChange={(event, values, reason) => onChange(values || undefined)}
                options={dispositions || []}
                disableClearable
                disabled={formSubmitting}
                getOptionLabel={(option) => option?.title || ''}
                filterSelectedOptions
                renderOption={({ key, ...optionProps }, option, state) => (
                  <li {...optionProps} key={`${option?.code}-${option?.subCode}`}>
                    <div>
                      <Typography variant='body2' color='textPrimary' component='p'>
                        {option.title}
                      </Typography>

                      <Typography variant='caption' color='textSecondary' component='p'>
                        {option.description}
                      </Typography>
                    </div>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id='disposition'
                    name={name}
                    label='Disposition'
                    required={true}
                    error={Boolean(errors.disposition)}
                    helperText={errors.disposition?.message}
                    variant='outlined'
                  />
                )}
              />
            )}
          />
        </Grid>

        {viewFlag === OutcomeType.Callback && (
          <Grid size={12}>
            <ControlledCheckbox
              name='callbackAssignment'
              label='Assign to Self?'
              control={control}
              disabled={formSubmitting}
            />
          </Grid>
        )}

        {(viewFlag === OutcomeType.Callback || viewFlag === OutcomeType.Snoozed) && (
          <>
            <Grid size={12}>
              <Controller
                name='callbackEndpoint'
                control={control}
                rules={{ required: 'A callback endpoint is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant='outlined'
                    id='callbackEndpoint'
                    label='Callback Endpoint'
                    disabled={formSubmitting}
                    required={true}
                    error={Boolean(errors.callbackEndpoint)}
                    helperText={errors.callbackEndpoint?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name='scheduled'
                control={control}
                rules={{
                  required: 'A callback date/ time is required',
                  validate: (value) => {
                    if (value === null) {
                      return 'A callback date/ time is required';
                    }
                    if (diallingHours === undefined) {
                      return 'Invalid Date';
                    }
                    const date = DateTime.fromISO(value as string);
                    if (!date.isValid) {
                      return 'Invalid Date';
                    }
                    // shouldDisableDate only blocks calendar clicks; re-check here to also block manual text entry.
                    if (disableDates(date)) {
                      return 'Callbacks cannot be scheduled on this day';
                    }
                    const diallingDay = diallingHours.diallingDays[weekdayNumberToDay[date.weekday]];
                    if (diallingDay === null || diallingDay === undefined) {
                      return 'Invalid Date';
                    }
                    if (date.hour > diallingDay.startTimeHour && date.hour < diallingDay.endTimeHour) {
                      return true;
                    }
                    if (
                      (date.hour === diallingDay.startTimeHour && date.minute >= diallingDay.startTimeMin) ||
                      (date.hour === diallingDay.endTimeHour && date.minute < diallingDay.endTimeMin)
                    ) {
                      return true;
                    }

                    const startHour = formatTimeComponent(diallingDay.startTimeHour);
                    const endHour = formatTimeComponent(diallingDay.endTimeHour);
                    const startMinute = formatTimeComponent(diallingDay.startTimeMin);
                    const endMinute = formatTimeComponent(diallingDay.endTimeMin);
                    const startTime = `${startHour}:${startMinute}`;
                    const endTime = `${endHour}:${endMinute}`;

                    return `Callback schedule time must be between ${startTime} and ${endTime}`;
                  },
                }}
                render={({ field: { value, onChange, ref, ...field } }) => (
                  <DateTimePicker
                    {...field}
                    value={value ? DateTime.fromISO(value) : null}
                    onChange={(date) => onChange(date?.toISO() ?? '')}
                    label='Callback Schedule'
                    format='dd/MM/yyyy hh:mm a'
                    disabled={formSubmitting}
                    disablePast={true}
                    ampm={false}
                    shouldDisableDate={disableDates}
                    shouldDisableTime={shouldDisableTime}
                    inputRef={ref}
                    slotProps={{
                      actionBar: { actions: ['clear'] },
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        required: true,
                        error: Boolean(errors.scheduled),
                        helperText: scheduledHelperText,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </>
        )}

        {viewFlag === OutcomeType.Callback && (
          <Grid size={12}>
            <Controller
              name='notes'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  id='notes'
                  label='Notes'
                  disabled={formSubmitting}
                  error={Boolean(errors.notes)}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </Grid>
        )}

        {viewFlag === OutcomeType.Excluded && (
          <>
            <Grid size={12}>
              <Controller
                name='exclusionList'
                control={control}
                rules={{ required: 'An exclusion list must be selected' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id='exclusionList'
                    fullWidth
                    select
                    variant='outlined'
                    label='Exclusion List'
                    disabled={formSubmitting}
                    error={Boolean(errors.exclusionList)}
                    required={true}
                    helperText={errors.exclusionList?.message}>
                    {exclusionListItems}
                  </TextField>
                )}
              />
            </Grid>
          </>
        )}

        {isUnknownLead === true && !disableNewLead && (
          <>
            <Grid size={12}>
              <Controller
                name='leadEndpoint'
                control={control}
                rules={{
                  required: requireLeadCreation ? 'Lead endpoint is required.' : false,
                  validate: (value) => {
                    const leadName = getValues().leadName;
                    const leadExternalId = getValues().leadExternalId;
                    return leadName && leadExternalId && !value ? 'Lead endpoint is required.' : true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant='outlined'
                    id='leadEndpoint'
                    name='leadEndpoint'
                    label='Lead Endpoint'
                    disabled={formSubmitting || requireLeadCreation}
                    value={field.value}
                    required={Boolean(requireLeadCreation)}
                    error={Boolean(errors.leadEndpoint)}
                    helperText={errors.leadEndpoint?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name='leadName'
                control={control}
                rules={{
                  required: requireLeadCreation ? 'Lead name is required.' : false,
                  validate: (value) => {
                    const leadEndpoint = getValues().leadEndpoint;
                    const leadExternalId = getValues().leadExternalId;
                    return leadEndpoint && leadExternalId && !value ? 'Lead name is required.' : true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant='outlined'
                    id='leadName'
                    name='leadName'
                    label='Lead Name'
                    disabled={formSubmitting}
                    required={Boolean(requireLeadCreation)}
                    error={Boolean(errors.leadName)}
                    helperText={errors.leadName?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name='leadExternalId'
                control={control}
                rules={{
                  required: requireLeadCreation ? 'Lead external ID is required.' : false,
                  validate: (value) => {
                    const leadEndpoint = getValues().leadEndpoint;
                    const leadName = getValues().leadName;
                    return leadEndpoint && leadName && !value ? 'External ID is required.' : true;
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant='outlined'
                    id='leadExternalId'
                    label='External ID'
                    disabled={formSubmitting}
                    value={field.value || ''}
                    required={Boolean(requireLeadCreation)}
                    error={Boolean(errors.leadExternalId)}
                    helperText={errors.leadExternalId?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={12}>
              <Controller
                name='leadTimezone'
                control={control}
                rules={{ required: requireLeadCreation ? 'Lead timezone is required.' : false }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    value={field.value || null}
                    fullWidth
                    options={timezoneList}
                    filterSelectedOptions
                    disabled={formSubmitting}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                      <TextField
                        {...params}
                        id='leadTimezone'
                        label='Timezone'
                        required={Boolean(requireLeadCreation)}
                        error={Boolean(timezoneFetchError) || Boolean(errors.leadTimezone)}
                        helperText={timezoneFetchError || errors.leadTimezone?.message}
                        variant='outlined'
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </>
        )}

        {AdditionalFields !== undefined && (
          <AdditionalFields channelType={channelType} formSubmitting={formSubmitting} />
        )}

        <Grid size={12}>
          <ControlledCheckbox
            name='hasSystemIssue'
            label='I had a system issue during this contact interaction?'
            control={control}
            disabled={formSubmitting}
          />
        </Grid>

        {hasSystemIssue && (
          <Grid size={12}>
            <Controller
              control={control}
              name='systemIssueDescription'
              rules={{ required: 'A system issue description is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  id='systemIssueDescription'
                  label='System Issue Description'
                  disabled={formSubmitting}
                  required={true}
                  error={Boolean(errors.systemIssueDescription)}
                  helperText={errors.systemIssueDescription?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={12}>
          <LoadingButton
            fullWidth
            variant='contained'
            disableElevation
            disabled={disableSubmit}
            loading={formSubmitting}
            onClick={() => handleOutcomeSubmit()()}
            color='primary'>
            Dispose and continue
          </LoadingButton>
        </Grid>

        <Grid size={12}>
          <LoadingButtonDropdown
            title='Go to State'
            fullWidth
            items={agentStateList}
            disabled={disableSubmit}
            loading={formSubmitting}
            onChange={(status) => handleOutcomeSubmit(status)()}
          />
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default OutcomeCapture;
